// Single source of truth for the per-file document upload limit.
//
// This module exists so the limit cannot drift between the file picker, the
// signed-upload authorization, the finalize endpoint, the document reader's own
// handling, customer error messages and the tests. Every one of those reads
// MAX_UPLOAD_BYTES from here — there are no second copies of the number.
//
// ⚠️ PRODUCTION STORAGE PRECONDITION
// The application is prepared for 25 MB per FILE (not per packet). The
// production Supabase "order-documents" bucket is currently limited to about
// 20 MiB, so a file between ~20 MiB and 25 MB will be authorized by this app and
// then REJECTED by storage at the direct PUT. The bucket's own file size limit
// must be raised to at least 25 MB (26214400 bytes) before this workflow is
// production-ready. Nothing here silently falls back to a smaller number: the
// app asks for 25 MB and reports the storage refusal per file if the bucket is
// still short.
const MAX_UPLOAD_MB = 25;
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024; // 26214400

// What the bucket must be raised to before deployment, for the operator note.
const REQUIRED_BUCKET_BYTES = MAX_UPLOAD_BYTES;
const BUCKET_NAME = "order-documents";

// ── MIME RESOLUTION ────────────────────────────────────────────────────────────
// The production order-documents bucket enforces an allowlist that does NOT
// include application/octet-stream. Browsers frequently report "" or
// application/octet-stream for a perfectly ordinary .pdf/.docx/.heic, so sending
// the browser's value straight through would have the bucket reject a supported
// file at the signed PUT — after the order already exists.
//
// EXT_TO_MIME is the authority: a recognised extension determines the stored
// Content-Type. The browser's value is only used to REJECT (an explicitly
// unsupported declared type is never relabelled into a supported one).
const EXT_TO_MIME = {
  pdf: "application/pdf",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  heic: "image/heic",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};
// Exactly the bucket's allowlist — nothing else may be sent as Content-Type.
const ALLOWED_MIME = Object.values(EXT_TO_MIME).filter((v, i, a) => a.indexOf(v) === i);
const ALLOWED_EXT = Object.keys(EXT_TO_MIME);
// Values a browser uses when it does not know: treat as "unknown", not as a type.
const GENERIC_MIME = ["", "application/octet-stream", "binary/octet-stream", "application/unknown"];

function extOf(name) {
  const n = String(name == null ? "" : name).toLowerCase().trim();
  const i = n.lastIndexOf(".");
  return i > -1 ? n.slice(i + 1) : "";
}

// Normalise the well-known aliases browsers emit.
function normalizeMime(mime) {
  const m = String(mime == null ? "" : mime).toLowerCase().split(";")[0].trim();
  if (m === "image/jpg" || m === "image/pjpeg") return "image/jpeg";
  if (m === "image/x-png") return "image/png";
  if (m === "application/x-pdf" || m === "application/acrobat") return "application/pdf";
  if (m === "image/heif") return "image/heic";
  return m;
}

// Resolve the Content-Type to store an upload under.
// Returns { ok: true, mime } or { ok: false, reason }.
//   * a recognised extension decides the type (so a blank/generic browser MIME
//     never reaches the bucket)
//   * a declared type that is supported but disagrees with the extension loses to
//     the extension, which is what the bucket allowlist is keyed on
//   * a declared type that is NOT supported is rejected outright — an unsupported
//     file is never relabelled as a supported one
//   * an unrecognised extension is rejected, even if the browser claims a
//     supported type, because the stored object would be ambiguous
function resolveMimeType(name, browserMime) {
  const ext = extOf(name);
  const declared = normalizeMime(browserMime);
  const known = EXT_TO_MIME[ext];
  if (!known) return { ok: false, reason: "unsupported_extension" };
  if (!GENERIC_MIME.includes(declared) && !ALLOWED_MIME.includes(declared)) {
    return { ok: false, reason: "unsupported_mime" };
  }
  return { ok: true, mime: known };
}

function fileTypeAllowed(name, mime) {
  return resolveMimeType(name, mime).ok;
}

function mb(bytes) {
  return (Math.max(0, Number(bytes) || 0) / (1024 * 1024)).toFixed(1);
}

// One wording for an oversized file, used by the picker, the server and tests —
// always naming the file, its actual size and the limit.
function oversizeMessage(name, sizeBytes) {
  return `"${name}" is ${mb(sizeBytes)} MB, over the ${MAX_UPLOAD_MB} MB per-file limit.`;
}

module.exports = {
  MAX_UPLOAD_MB,
  MAX_UPLOAD_BYTES,
  REQUIRED_BUCKET_BYTES,
  BUCKET_NAME,
  ALLOWED_EXT,
  ALLOWED_MIME,
  EXT_TO_MIME,
  GENERIC_MIME,
  extOf,
  normalizeMime,
  resolveMimeType,
  fileTypeAllowed,
  oversizeMessage,
  mb,
};
